import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { EMAIL_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import type { CheckoutFormValuesProps } from "@/react/components/Paywalls/Checkout/types/CheckoutFormValuesProps";
import { isManualEmailError } from "../helpers";
import { type EmailVerificationErrorType } from "../types";

interface UseEmailVerificationInitialStateProps {
  onAuthenticationLinkClick?: () => void;
}

export const useEmailVerificationInitialState = ({
  onAuthenticationLinkClick,
}: UseEmailVerificationInitialStateProps) => {
  const {
    trigger,
    formState: { errors },
    watch,
  } = useFormContext<CheckoutFormValuesProps>();

  const emailValue = watch("email");

  const handleBlur = useCallback(() => {
    // React Hook Form automatically validates on blur when the field has required/pattern options
    // We don't need to manually trigger validation here - it would cause duplicate validation
    // and potentially block the form unnecessarily
  }, []);

  const handleAuthenticationClick = useCallback(() => {
    onAuthenticationLinkClick?.();
  }, [onAuthenticationLinkClick]);

  useEffect(() => {
    const validateEmail = async () => {
      // Don't trigger validation if there's a manual error (e.g., from Mailgun validation)
      // This preserves the Mailgun error message
      // Also don't trigger validation if the email is empty or doesn't match the pattern
      // to prevent blocking the form when users are still typing or have invalid input

      const trimmedEmail = emailValue?.trim();
      if (
        trimmedEmail &&
        EMAIL_REGEX.test(trimmedEmail) &&
        errors.email &&
        !isManualEmailError(errors.email.type as EmailVerificationErrorType)
      ) {
        await trigger("email");
      }
    };

    void validateEmail();
  }, [emailValue, trigger, errors.email]);

  return {
    handleBlur,
    handleAuthenticationClick,
  };
};
